/**
 * Sentry on web. Imported first in main.tsx, so the SDK is in place before
 * anything else can throw.
 *
 * - Off in development (`appEnv('development')`: the local dev server) and
 *   without a `VITE_SENTRY_DSN`, so local errors don't use the quota. Production
 *   builds read `VITE_APP_ENVIRONMENT=production` from `.env.production`.
 * - Privacy: user info, cookies, HTTP headers and bodies, and URL query
 *   strings are not collected. The user is set by id only, through the shared
 *   observability manager, which also scrubs what it reports.
 * - Tracing at 100% during the beta; lower it before the public launch.
 *   Frontend only: no `sentry-trace`/`baggage` headers go to the API
 *   (`tracePropagationTargets: []`, PRD decision 7). The backend has its own
 *   monitoring (Laravel Nightwatch), and the SDK's default would otherwise
 *   add them to same-origin and localhost requests.
 * - Session Replay on errors only: the SDK buffers the last minute of the
 *   session and sends it only when an error is captured
 *   (`replaysSessionSampleRate: 0`). The replay keeps the SDK's privacy
 *   defaults — all text and inputs masked, media blocked, no network bodies
 *   or headers — so names, measurements and notes never leave the browser.
 * - Reports from the observability manager reach Sentry through
 *   `SentryObservabilityProvider`, registered in initializeWebServices().
 */

import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { appEnv } from '@personal-trainer-app/shared/src/utils/env';

const dsn = import.meta.env.VITE_SENTRY_DSN;

if (dsn && !appEnv('development')) {
  Sentry.init({
    dsn,
    environment: appEnv(),
    release: import.meta.env.VITE_APP_VERSION || undefined,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
    },
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}
